import React from 'react';
import { makeStyles } from '@mui/styles';
import IconButton from '@mui/material/IconButton';
import { connect, useDispatch, useSelector } from 'react-redux'
import Snackbar from '@mui/material/Snackbar';
import CloseIcon from '@mui/icons-material/Close';

import Design from "./design";
import ActionSidebar from "./sidebar";

import { fetchDesigns, fetchCatalogues, showAlert, saveImage, saveEdit, saveText, saveIfElse, saveMjml } from "../actions"
import { getSelectedRowContent, isImage, isText, isIf, isIfElse, isMjml } from "../selectors";

const useStyles = makeStyles(theme => ({
  root: {
    flexGrow: 1,
  },
}));

const Home = ({ onFetchDesigns, onFetchCatalogues, alert, onHideAlert, theme, onToggleTheme }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  let fetched = false;

  React.useEffect(() => {
    if (!fetched) {
      onFetchDesigns();
      onFetchCatalogues();
      fetched = true;
    }
  }, []);

  const handleClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    onHideAlert();
  };

  // Coordinates the row editor (opened from the sidebar rail, rendered below the canvas in Design)
  // and centralizes the "checkpoint to reset to" that used to live inside each property editor.
  const selectedRowContent = useSelector(getSelectedRowContent);
  const contentIsImage = useSelector(isImage);
  const contentIsText = useSelector(isText);
  const contentIsIf = useSelector(isIf);
  const contentIsIfElse = useSelector(isIfElse);
  const contentIsMjml = useSelector(isMjml);

  const [editorOpen, setEditorOpen] = React.useState(false);
  const [resetKey, setResetKey] = React.useState(0);
  const originalConfigRef = React.useRef(null);
  const selectedRowId = selectedRowContent && selectedRowContent.id;

  React.useEffect(() => {
    originalConfigRef.current = selectedRowContent ? structuredClone(selectedRowContent.config) : null;
    setEditorOpen(!!selectedRowContent);
  }, [selectedRowId]);

  const handleToggleEditor = () => setEditorOpen((open) => !open);
  const handleCloseEditor = () => setEditorOpen(false);

  const handleSave = () => {
    if (!selectedRowContent) return;
    const { id, config } = selectedRowContent;
    if (contentIsImage) {
      dispatch(saveImage({ rowId: id, value: config }));
    } else if (contentIsText) {
      dispatch(saveEdit({ rowId: id, value: config.text }));
      dispatch(saveText({ rowId: id, text: config.text }));
    } else if (contentIsIf || contentIsIfElse) {
      dispatch(saveIfElse({ rowId: id, condition: config.condition }));
    } else if (contentIsMjml) {
      dispatch(saveMjml({ rowId: id, mjml: config.mjml, html: config.html }));
    }
    originalConfigRef.current = structuredClone(config);
    dispatch(showAlert(true));
  };

  const handleReset = () => {
    if (!selectedRowContent || !originalConfigRef.current) return;
    const id = selectedRowContent.id;
    const original = originalConfigRef.current;
    if (contentIsImage) {
      dispatch(saveImage({ rowId: id, value: original }));
    } else if (contentIsText) {
      dispatch(saveEdit({ rowId: id, value: original.text }));
      dispatch(saveText({ rowId: id, text: original.text }));
    } else if (contentIsIf || contentIsIfElse) {
      dispatch(saveIfElse({ rowId: id, condition: original.condition }));
    } else if (contentIsMjml) {
      dispatch(saveMjml({ rowId: id, mjml: original.mjml, html: original.html }));
    }
    setResetKey((key) => key + 1);
  };

  return (
    <div className={classes.root}>
      <div style={{ display: 'flex' }}>
        <ActionSidebar
          editorOpen={editorOpen}
          onToggleEditor={handleToggleEditor}
          onSave={handleSave}
          onReset={handleReset}
          theme={theme}
          onToggleTheme={onToggleTheme}
        />
        <div style={{ flex: 1, minWidth: 0, padding: 24 }}>
          <Design
            editorOpen={editorOpen}
            onCloseEditor={handleCloseEditor}
            resetKey={resetKey}
          />
        </div>
      </div>
      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        open={alert.open}
        onClose={handleClose}
        autoHideDuration={6000}
        message="Design Saved Successfully"
        action={
          <React.Fragment>
            <IconButton size="small" aria-label="close" color="inherit" onClick={handleClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </React.Fragment>
        }
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
      alert: state.alert
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
      onFetchDesigns: () => {
        dispatch(fetchDesigns());
      },
      onFetchCatalogues: () => {
        dispatch(fetchCatalogues());
      },
      onHideAlert: () => {
        dispatch(showAlert(false));
      }
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Home);
